import React, { useEffect, useState } from 'react';
import {
  Box, Stack, Typography, Table, TableContainer, TableHead, TableRow, TableCell, TableBody, Chip,
  Select, MenuItem, Button,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ReplayIcon from '@mui/icons-material/Replay';
import UndoIcon from '@mui/icons-material/Undo';
import { toast } from 'react-toastify';
import { API } from 'utilities/api';
import {
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
} from '@mui/material';
import { LoadingOverlay, EmptyState, GlassIconButton } from 'dashboard/components/common';
import RefundDialog from './RefundDialog';

const fmtMoney = (cents, ccy) => {
  if (cents == null) return '—';
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: (ccy || 'usd').toUpperCase(),
    }).format((cents || 0) / 100);
  } catch (_) {
    return `${(cents / 100).toFixed(2)} ${ccy || ''}`;
  }
};

const fmtDate = (ts) => (ts ? new Date(ts).toLocaleDateString() : '—');

const PAGE_SIZE = 50;

export default function SubscriptionsTab() {
  const [status, setStatus] = useState('all');
  const [rows, setRows] = useState([]);
  const [stripeOn, setStripeOn] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [lastId, setLastId] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [refundFor, setRefundFor] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [reloadCounter, setReloadCounter] = useState(0);

  // First page — also fires when the status filter flips or an action
  // hits, which is what the reloadCounter is for.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    API.adminListSubs({ status, limit: PAGE_SIZE })
      .then((res) => {
        if (cancelled) return;
        setRows(res?.items || []);
        setStripeOn(res?.stripe !== false);
        setHasMore(!!res?.has_more);
        setLastId(res?.last_id || null);
      })
      .catch((err) => toast.error(err?.message || 'Failed to load subscriptions', { toastId: 'admin-subs' }))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [status, reloadCounter]);

  const loadMore = () => {
    if (!hasMore || !lastId || loadingMore) return;
    setLoadingMore(true);
    API.adminListSubs({ status, limit: PAGE_SIZE, starting_after: lastId })
      .then((res) => {
        setRows((cur) => [...cur, ...(res?.items || [])]);
        setHasMore(!!res?.has_more);
        setLastId(res?.last_id || null);
      })
      .catch((err) => toast.error(err?.message || 'Failed to load more subscriptions'))
      .finally(() => setLoadingMore(false));
  };

  // Two cancel paths: soft (cancel_at_period_end) is the default and
  // matches Stripe's billing-aware behavior; immediate hits
  // `stripe.subscriptions.cancel` and revokes access right now.
  const cancelSub = (sub, immediate = false) => {
    setBusyId(sub.id);
    API.adminCancelSub({ subscription_id: sub.id, immediate })
      .then(() => {
        toast.success(immediate
          ? 'Subscription cancelled immediately'
          : 'Subscription set to cancel at period end');
        setReloadCounter((n) => n + 1);
      })
      .catch((err) => toast.error(err?.message || 'Failed to cancel subscription'))
      .finally(() => { setBusyId(null); setConfirm(null); });
  };

  const resumeSub = (sub) => {
    setBusyId(sub.id);
    API.adminResumeSub({ subscription_id: sub.id })
      .then(() => {
        toast.success('Subscription resumed');
        setReloadCounter((n) => n + 1);
      })
      .catch((err) => toast.error(err?.message || 'Failed to resume subscription'))
      .finally(() => setBusyId(null));
  };

  // `amount_cents = null` → full refund of the most recent payment.
  // Anything else is a partial refund and goes straight to Stripe.
  const refundSub = (sub, amount_cents) => {
    setBusyId(sub.id);
    const payload = { subscription_id: sub.id };
    if (Number.isFinite(amount_cents) && amount_cents > 0) {
      payload.amount_cents = amount_cents;
    }
    API.adminRefundLatestSub(payload)
      .then((res) => {
        const amount = res?.amount != null
          ? new Intl.NumberFormat(undefined, {
              style: 'currency',
              currency: (res.currency || 'usd').toUpperCase(),
            }).format(res.amount / 100)
          : null;
        toast.success(amount ? `Refunded ${amount}` : 'Refund issued');
      })
      .catch((err) => toast.error(err?.message || 'Failed to refund payment'))
      .finally(() => { setBusyId(null); setRefundFor(null); });
  };

  if (!stripeOn) {
    return <EmptyState>Stripe isn't configured on this server. Subscriptions are read directly from Stripe.</EmptyState>;
  }

  return (
    <Box>
      <Stack direction='row' alignItems='center' justifyContent='space-between' mb='12px'>
        <Stack direction='row' spacing='10px' alignItems='center'>
          <Typography variant='body2' color='text.secondary'>Status</Typography>
          <Select size='small' value={status} onChange={(e) => setStatus(e.target.value)} sx={{ minWidth: 140 }}>
            <MenuItem value='all'>All</MenuItem>
            <MenuItem value='active'>Active</MenuItem>
            <MenuItem value='trialing'>Trialing</MenuItem>
            <MenuItem value='past_due'>Past due</MenuItem>
            <MenuItem value='canceled'>Canceled</MenuItem>
            <MenuItem value='unpaid'>Unpaid</MenuItem>
          </Select>
        </Stack>
        <Typography variant='caption' color='text.secondary'>
          {rows.length} subscriptions{hasMore ? ' (more available)' : ''}
        </Typography>
      </Stack>

      <LoadingOverlay loading={loading} minHeight={240}>
        {rows.length === 0 && !loading ? (
          <EmptyState>No subscriptions in this filter.</EmptyState>
        ) : (
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size='small' sx={{ minWidth: 720 }}>
            <TableHead>
              <TableRow>
                <TableCell>Customer</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Plan</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Period</TableCell>
                <TableCell>Cancels at end</TableCell>
                <TableCell align='right'>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((s) => {
                const item = s.items[0];
                // A row is busy while one of its own actions is in flight, and
                // "ended" once Stripe says there is nothing left to cancel.
                // Both are spelled out to the user rather than just greying the
                // control out with no explanation.
                const busy = busyId === s.id;
                const ended = ['canceled', 'incomplete_expired'].includes(s.status);
                const busyReason = 'This subscription is already being updated — give it a moment.';
                const cancelReason = busy
                  ? busyReason
                  : ended
                    ? `This subscription is already ${s.status.replace(/_/g, ' ')} — there is nothing left to cancel.`
                    : '';
                return (
                  <TableRow key={s.id} hover>
                    <TableCell>
                      <Typography variant='body2'>{s.customer?.email || s.customer?.name || s.customer?.id}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip size='small' color={s.status === 'active' ? 'success' : s.status === 'past_due' ? 'error' : 'default'} label={s.status} />
                    </TableCell>
                    <TableCell>{item?.nickname || item?.price_id || '—'}</TableCell>
                    <TableCell>{item ? `${fmtMoney(item.unit_amount, item.currency)} / ${item.interval}` : '—'}</TableCell>
                    <TableCell>{fmtDate(s.current_period_start)} → {fmtDate(s.current_period_end)}</TableCell>
                    <TableCell>{s.cancel_at_period_end ? 'yes' : 'no'}</TableCell>
                    <TableCell align='right'>
                      <GlassIconButton
                        size='small'
                        color='warning'
                        label='Refund latest payment'
                        disabled={busy}
                        disabledReason={busy ? busyReason : ''}
                        onClick={() => setRefundFor(s)}
                      >
                        <UndoIcon fontSize='small' />
                      </GlassIconButton>
                      {s.cancel_at_period_end ? (
                        <GlassIconButton
                          size='small'
                          label='Resume — keep billing past period end'
                          disabled={busy}
                          disabledReason={busy ? busyReason : ''}
                          onClick={() => resumeSub(s)}
                        >
                          <ReplayIcon fontSize='small' />
                        </GlassIconButton>
                      ) : (
                        <GlassIconButton
                          size='small'
                          color='error'
                          label='Cancel at period end'
                          disabled={busy || ended}
                          disabledReason={cancelReason}
                          onClick={() => setConfirm({ sub: s })}
                        >
                          <CloseIcon fontSize='small' />
                        </GlassIconButton>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
            </Table>
          </TableContainer>
        )}
      </LoadingOverlay>
      {hasMore && (
        <Box mt='14px' textAlign='center'>
          <Button
            variant='outlined'
            size='small'
            disabled={loadingMore}
            onClick={loadMore}
          >
            {loadingMore ? 'Loading…' : 'Load more'}
          </Button>
        </Box>
      )}
      {/* Two-action cancel dialog: soft cancel = period-end (default,
          safer, lets Stripe handle prorations); immediate = revoke now.
          Kept inline rather than building a CancelDialog wrapper —
          it's the only place in the app with two-mode cancel. */}
      <Dialog open={!!confirm} onClose={() => setConfirm(null)} maxWidth='xs' fullWidth>
        <DialogTitle>Cancel subscription?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirm && (
              <>
                Customer: <strong>{confirm.sub.customer?.email || confirm.sub.customer?.id}</strong>.
                <br />
                <em>Cancel at period end</em> keeps them active through {fmtDate(confirm.sub.current_period_end)}{' '}
                and stops future billing — you can resume before then.
                <br />
                <em>Cancel immediately</em> revokes access right now (no refund issued — handle separately).
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: '6px', justifyContent: 'flex-end' }}>
          <Button onClick={() => setConfirm(null)} disabled={!!busyId}>Keep active</Button>
          <Button
            color='error'
            disabled={!!busyId}
            onClick={() => confirm && cancelSub(confirm.sub, true)}
          >
            {busyId ? 'Working…' : 'Cancel immediately'}
          </Button>
          <Button
            variant='contained'
            color='error'
            disabled={!!busyId}
            onClick={() => confirm && cancelSub(confirm.sub, false)}
          >
            {busyId ? 'Working…' : 'Cancel at period end'}
          </Button>
        </DialogActions>
      </Dialog>
      <RefundDialog
        open={!!refundFor}
        sub={refundFor}
        busy={!!busyId}
        onConfirm={(amount_cents) => refundSub(refundFor, amount_cents)}
        onCancel={() => setRefundFor(null)}
      />
    </Box>
  );
}
